import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'
import { AcknowledgmentStatusPanel } from 'partner360/features/acknowledgmentStatusDialog'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { invalidateFinancialCockpitData } from '../../../../model/financialCockpitSlice'
import { selectCockpitPartnerData, selectEffectiveCockpitFYear } from '../../../../model/selectors'
import { useNextOfferingBlockedAction } from '../../../components/nextOfferingAction'
import { useAttentionDetailOffering } from '../../hooks/useAttentionDetailOffering'
import { AttentionStatusActions } from '../attentionStatusActions'

import styles from './AttentionStatusAcknowledgment.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail

/**
 * The acknowledgment detail of the `Offering X is not fully acknowledged` status: the same panel
 * the H2 review opens for its `jbp-pat-acknowledgment` action, embedded in the expanded tile
 * instead of a dialog.
 */
export const AttentionStatusAcknowledgment = () => {
  const dispatch = useAppDispatch()
  /* The cockpit's own partner row rather than the page's: the manager cockpit opens this section
     for one of its partners, and its page stands on none of them. */
  const partnerId = useSelector(selectCockpitPartnerData)[0]?.PartnerId ?? ''
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const activeOffering = useAttentionDetailOffering()
  const blockedAction = useNextOfferingBlockedAction(activeOffering)

  /* Signing or dropping a signer moves the status itself, so the cockpit's payload is refetched
     rather than only the panel's own list. */
  const handleUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])

  /* Same as the completeness section: the panel is signed per offering, so a partner showing none
     gets a sentence rather than an empty card. */
  if (!partnerId || !activeOffering) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>
          <Translation span text={KEYS.noOffering} />
        </p>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <AcknowledgmentStatusPanel
        partnerId={partnerId}
        fYear={fYear}
        offering={activeOffering}
        showPartnerSummary={false}
        onUpdated={handleUpdated}
        blockedAction={blockedAction}
      />

      {/* Signing and chasing a signature are both done on the panel's own rows, so the bar keeps
      neither. It is still drawn: the card's corner is where the way to the partner's page is
      offered, and that is the card's, not the status'. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.acknowledgment}
        hidden={['acknowledge', 'remindAcknowledgment']}
      />
    </div>
  )
}

AttentionStatusAcknowledgment.displayName = 'AttentionStatusAcknowledgment'
